import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { ImageWithFocalPointType } from "@swo/shared";
import {
  centeredFocalPoint,
  FocalPointPosition,
  getFocalPoint,
} from "../../_shared-components/image-related/ImageWithFocalPoint";
import { pipe } from "fp-ts/lib/function";
import { option } from "fp-ts";
import { css } from "@emotion/react";
import { CSS_MQ } from "../../../styles/breakpoints";
import { toggleableVisibility } from "./styles";
import cn from "classnames";
import { createFallbackImage, createJpgLink } from "../../_shared-components/image-related/createJpgAndWebpLinks";
import { ImageSourceElements } from "./ImageSourceElements";
import React, { CSSProperties } from "react";

export const PictureHeroImageVisualElement: ReactTFC<{
  desktop: ImageWithFocalPointType | undefined;
  mobile: ImageWithFocalPointType | undefined;
}> = props => {
  const desktopFocalPoint: FocalPointPosition =
    (props.desktop && props.desktop.__typename === "UtilityImageWithFocalPoint" && getFocalPoint(props.desktop)) ||
    centeredFocalPoint;

  const mobileFocalPoint: FocalPointPosition =
    (props.mobile && props.mobile.__typename === "UtilityImageWithFocalPoint" && getFocalPoint(props.mobile)) ||
    desktopFocalPoint;

  const imgSrc = pipe(
    option.fromNullable(props.desktop),
    option.alt(() => option.fromNullable(props.mobile)),
    option.map(asset => asset.image.url),
    option.getOrElseW(() => undefined),
  );

  const alt =
    props.desktop?.image.description ||
    props.mobile?.image.description ||
    props.desktop?.image.title ||
    props.mobile?.image.title;

  return imgSrc || props.mobile || props.desktop ? (
    <div
      css={css`
        overflow: hidden;
        position: relative;
      `}
      className={"visual"}
      style={
        {
          "--opMobile": mobileFocalPoint.asCssString(),
          "--opDesktop": desktopFocalPoint.asCssString(),
        } as CSSProperties
      }
    >
      {props.mobile && (
        <img
          className={cn({ "visible-mobile": props.mobile })}
          css={blurredImageStyles}
          src={createJpgLink(props.mobile.image.url, 200, 10)}
          alt={alt}
          style={{
            backgroundPosition: "var(--op)",
          }}
        />
      )}

      {props.desktop && (
        <img
          className={cn({ "visible-desktop": props.desktop })}
          css={blurredImageStyles}
          src={createJpgLink(props.desktop.image.url, 50, 10)}
          alt={alt}
          style={{
            backgroundPosition: "var(--op)",
          }}
        />
      )}

      <picture
        css={css`
          display: flex; // avoid line-height

          position: absolute;
          width: 100%;
          height: 100%;
          left: 0;
          top: 0;

          z-index: 2;
        `}
      >
        {props.mobile && <ImageSourceElements plainImageUrl={props.mobile.image.url} device={"mobile"} />}
        {props.desktop && <ImageSourceElements plainImageUrl={props.desktop.image.url} device={"desktop"} />}

        <img
          className={cn({ "visible-mobile": props.mobile, "visible-desktop": props.desktop })}
          css={imageStyles}
          alt={alt}
          src={imgSrc ? createFallbackImage(imgSrc, 1600) : undefined}
        />
      </picture>
    </div>
  ) : null;
};

const imageStyles = [
  css`
    width: 100%;
    height: 100%;
    object-fit: cover;

    --op: var(--opMobile);
    ${CSS_MQ.atLeastMd} {
      --op: var(--opDesktop);
    }
  `,
  toggleableVisibility,
];

const blurredImageStyles = [
  imageStyles,
  css`
    z-index: 1;

    --s: 20px;

    position: absolute;

    width: calc(100% + 2 * var(--s));
    height: calc(100% + 2 * var(--s));
    left: calc(-1 * var(--s));
    top: calc(-1 * var(--s));
    filter: blur(15px);
    overflow: hidden;
  `,
];
